import { styled } from 'styled-components';
import React, { FC, ReactNode, useState } from 'react';
import { Styling } from '@/styling';
import { flexGap } from '@/flexGap';

const Label = styled.label`
  ${Styling.typography('body')}
  color: ${({ theme }) => theme.palette.primary.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  cursor: pointer;
  user-select: none;

  &.disabled {
    cursor: not-allowed;
  }
`;

const Input = styled.input.attrs({
  type: 'radio',
})`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;

type DotProps = {
  hover?: boolean;
  checked?: boolean;
  disabled?: boolean;
};

const Dot = styled.span<DotProps>(({ hover, checked, disabled, theme }) => ({
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  height: '24px',
  width: '24px',
  backgroundColor: theme.palette.grayscale.white,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '50%',
  ...(hover
    ? {
        backgroundColor: theme.palette.primary.light5,
      }
    : {}),
  ...(checked
    ? {
        backgroundColor: theme.palette.grayscale.white,
        '&:after': {
          content: '""',
          position: 'absolute',
          backgroundColor: disabled
            ? theme.palette.primary.light2
            : theme.palette.primary.main,
          width: '14px',
          height: '14px',
          borderRadius: '50%',
        },
      }
    : {}),
  ...(disabled
    ? {
        backgroundColor: theme.palette.grayscale.light4,
        border: `1px solid ${theme.palette.grayscale.light3}`,
      }
    : {}),
}));

export type RadioButtonProps = {
  className?: string;
  name?: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?(checked: boolean): void;
  label?: ReactNode;
};

export const RadioButton: FC<RadioButtonProps> = ({
  className,
  name,
  checked,
  disabled,
  onChange,
  label,
}) => {
  const [isHovering, setIsHovering] = useState(false);

  if (disabled) {
    className += ' disabled';
  }

  return (
    <Label
      className={className}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Dot hover={isHovering} checked={checked} disabled={disabled} />
      {label}
      <Input
        name={name}
        disabled={disabled}
        checked={checked}
        onChange={(evt) => onChange?.(evt.target.checked)}
      />
    </Label>
  );
};

type FieldSetProps = {
  direction: 'horizontal' | 'vertical';
};

const FieldSet = styled.fieldset<FieldSetProps>`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: baseline;
  flex-direction: ${({ direction }) =>
    direction === 'horizontal' ? 'row' : 'column'};
  ${({ theme }) => flexGap(theme.spacing[2])};
  border: 0;
`;

export type RadioGroupProps = {
  name?: string;
  disabled?: boolean;
  value?: string;
  direction?: 'horizontal' | 'vertical';
  options: {
    value: string;
    label: ReactNode;
    disabled?: boolean;
  }[];
  onChange?(selected: string | null): void;
};

// TODO accessibility: focus
export const RadioGroup: FC<RadioGroupProps> = ({
  name,
  disabled,
  value,
  direction = 'horizontal',
  options,
  onChange,
}) => {
  const handleChange = (option: string, selected: boolean) => {
    if (selected) {
      onChange?.(option);
    }
  };

  return (
    <FieldSet direction={direction}>
      {options.map((option) => (
        <RadioButton
          key={option.value}
          name={name}
          checked={option.value === value}
          disabled={option.disabled || disabled}
          onChange={(selected) => handleChange(option.value, selected)}
          label={option.label}
        />
      ))}
    </FieldSet>
  );
};

const Label2 = styled.label`
  // ${Styling.typography('body')}
  //display: inline-flex;
  //align-items: center;
  //justify-content: center;
  // ${({ theme }) => flexGap(theme.spacing[1])};
  //cursor: pointer;
  user-select: none;

  ${Styling.typography('button')}
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[2])};
  text-transform: uppercase;
  //border: 0;
  border-radius: ${Styling.radii('button')};
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms,
    border 80ms;
  min-width: 100px;

  height: 44px;
  padding: 0 ${Styling.spacing(4)};

  &.lg {
    height: 56px;
    padding: 0 ${Styling.spacing(6)};
  }

  &.md {
    height: 44px;
    padding: 0 ${Styling.spacing(4)};
  }

  &.sm {
    height: 32px;
    padding: 0 ${Styling.spacing(2)};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }

  color: ${({ theme }) => theme.palette.primary.main};
  background-color: ${({ theme }) => theme.palette.primary.light5};
  border: 1px solid ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light4};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.light3};
  }

  &:disabled,
  &.loading {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: ${({ theme }) => theme.palette.disabled.text};
    border: 1px solid ${({ theme }) => theme.palette.disabled.dark1};

    &:hover {
      background-color: ${({ theme }) => theme.palette.disabled.main};
    }
  }
`;
export const RadioButton2: FC<RadioButtonProps> = ({
  className,
  name,
  checked,
  disabled,
  onChange,
  label,
}) => {
  if (disabled) {
    className += ' disabled';
  }

  return (
    <Label2 className={className}>
      {label}
      <Input
        name={name}
        disabled={disabled}
        checked={checked}
        onChange={(evt) => onChange?.(evt.target.checked)}
      />
    </Label2>
  );
};

export const RadioGroup2: FC<RadioGroupProps> = ({
  name,
  disabled,
  value,
  direction = 'horizontal',
  options,
  onChange,
}) => {
  const handleChange = (option: string, selected: boolean) => {
    if (selected) {
      onChange?.(option);
    }
  };

  return (
    <FieldSet direction={direction}>
      {options.map((option) => (
        <RadioButton2
          key={option.value}
          name={name}
          checked={option.value === value}
          disabled={option.disabled || disabled}
          onChange={(selected) => handleChange(option.value, selected)}
          label={option.label}
        />
      ))}
    </FieldSet>
  );
};
